import {
  AutocompleteListbox,
  AutocompleteOption,
  type AutocompleteRenderGroupParams,
  ListItemContent,
  ListItemDecorator,
  ListSubheader,
} from '@mui/joy';
import { createContext, forwardRef, Fragment, useContext } from 'react';
import { FixedSizeList, type ListChildComponentProps } from 'react-window';
import { Popper, type PopperProps } from '@mui/base';
import classNames from 'classnames';

import { EntityType } from '~/common/enums';

import { BuildingIcon, CompanyIcon, ContactIcon, TeamIcon } from '@/shared/icons/Icons';
import { type AutocompleteOption as AutocompleteOptionType } from '@/shared/hooks/autocomplete';

type GroupProps = React.PropsWithChildren<{
  style?: React.CSSProperties;
}>;

type LIProps = Omit<React.HTMLAttributes<HTMLLIElement>, 'color'>;

type OptionType = AutocompleteOptionType<string | number> & {
  type?: string;
};

export type VirtualRenderers<T extends OptionType> = {
  renderVirtualGroup?: (props: GroupProps, group: string) => React.ReactNode;
  renderVirtualItem?: (props: LIProps, option: T) => React.ReactNode;
};

const DEFAULT_OPTION_TYPES = {
  BUILDING: EntityType.Building,
  CENTRAL_COMPANY: EntityType.CentralCompany,
  COMPANY: EntityType.Company,
  CONTACT: EntityType.Contact,
  INVITATION: 'invitation',
  TEAM: EntityType.Team,
} as const;

const ITEM_SIZE = 36;
const LIST_BOX_PADDING = 8;
const OVERSCAN_COUNT = 10;

const OuterElementContext = createContext({});

const isRenderOption = <T extends OptionType>(data: unknown): data is [LIProps, T] => {
  if (!data || !Array.isArray(data) || data.length !== 2) return false;

  const [props, option] = data;

  const hasProps = !!props && typeof props === 'object';
  const hasOption =
    !!option &&
    typeof option === 'object' &&
    'label' in option &&
    typeof option.label === 'string' &&
    'value' in option &&
    (typeof option.value === 'string' || typeof option.value === 'number');

  return hasProps && hasOption;
};

const isRenderGroup = (data: unknown): data is AutocompleteRenderGroupParams =>
  !!data &&
  typeof data === 'object' &&
  'group' in data &&
  typeof data.group === 'string' &&
  'key' in data &&
  typeof data.key === 'string';

type DefaultGroupHeaderProps = GroupProps;

const DefaultGroupHeader: React.FC<DefaultGroupHeaderProps> = (props) => (
  <ListSubheader {...props} component="li" />
);

type DefaultOptionProps = LIProps & {
  type?: string;
};

const DefaultOption: React.FC<DefaultOptionProps> = ({ children, className, type, ...rest }) => {
  const decorator = (() => {
    switch (type) {
      case DEFAULT_OPTION_TYPES.CENTRAL_COMPANY:
      case DEFAULT_OPTION_TYPES.COMPANY:
      case DEFAULT_OPTION_TYPES.TEAM:
        return <CompanyIcon />;
      case DEFAULT_OPTION_TYPES.CONTACT:
        return <ContactIcon />;
      case DEFAULT_OPTION_TYPES.BUILDING:
        return <BuildingIcon />;
      case DEFAULT_OPTION_TYPES.INVITATION:
        return <TeamIcon />;
      default:
        return null;
    }
  })();

  return (
    <AutocompleteOption
      {...rest}
      className={classNames(
        className,
        type === DEFAULT_OPTION_TYPES.INVITATION ? 'text-red-500' : 'text-gray-800',
      )}
    >
      {!!decorator && <ListItemDecorator>{decorator}</ListItemDecorator>}
      <ListItemContent>{children}</ListItemContent>
    </AutocompleteOption>
  );
};

const renderRow = <T extends OptionType>(
  props: ListChildComponentProps,
  renderers: VirtualRenderers<T>,
) => {
  const defaultStyle = {
    ...props.style,
    gap: 0,
    top: typeof props.style.top === 'number' ? props.style.top + LIST_BOX_PADDING : props.style.top,
  };

  const dataSet = props.data[props.index];

  if (isRenderGroup(dataSet)) {
    const styledProps = { children: dataSet.group, style: defaultStyle };

    return (
      <Fragment key={dataSet.key}>
        {renderers.renderVirtualGroup ? (
          renderers.renderVirtualGroup(styledProps, dataSet.group)
        ) : (
          <DefaultGroupHeader {...styledProps} />
        )}
      </Fragment>
    );
  }

  if (!isRenderOption<T>(dataSet)) return null;

  const [liProps, option] = dataSet;

  const styledLIProps = {
    ...liProps,
    children: option.label,
    className: 'text-sm',
    style: defaultStyle,
  };

  return (
    <Fragment key={option.value}>
      {renderers.renderVirtualItem ? (
        renderers.renderVirtualItem(styledLIProps, option)
      ) : (
        <DefaultOption {...styledLIProps} type={option.type} />
      )}
    </Fragment>
  );
};

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);

  return (
    <AutocompleteListbox
      {...props}
      {...outerProps}
      ref={ref}
      component="div"
      sx={{
        '& ul': {
          flexShrink: 0,
          margin: 0,
          padding: 0,
        },
      }}
    />
  );
});

type Props<T extends OptionType = OptionType> = {
  anchorEl: PopperProps['anchorEl'];
  open: boolean;
  modifiers: PopperProps['modifiers'];
} & React.HTMLAttributes<HTMLElement> &
  VirtualRenderers<T>;

const VirtualListBoxAdapter = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { children, anchorEl, open, modifiers, renderVirtualGroup, renderVirtualItem, ...rest } =
    props;

  const items = children as [{ children: React.ReactElement[] | undefined }[]];
  const itemData: unknown[] = [];

  items[0].forEach((item) => {
    if (item) {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  });

  const itemCount = itemData.length;

  return (
    <Popper
      ref={ref}
      anchorEl={anchorEl}
      className="z-popperModal"
      modifiers={modifiers}
      open={open}
    >
      <OuterElementContext.Provider value={rest}>
        <FixedSizeList
          height={ITEM_SIZE * 16}
          innerElementType="ul"
          itemCount={itemCount}
          itemData={itemData}
          itemSize={ITEM_SIZE}
          outerElementType={OuterElementType}
          overscanCount={OVERSCAN_COUNT}
          width="100%"
        >
          {(childProps) => renderRow(childProps, { renderVirtualGroup, renderVirtualItem })}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </Popper>
  );
});

export default VirtualListBoxAdapter;
